<template>
    <div class="w-full min-h-screen bg-gray-100 px-6 py-5 text-gray-800">
        <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5">
            <div class="flex flex-wrap items-end gap-4">
                <div>
                    <h1 class="text-lg font-semibold text-dark-primary">Client Appendix</h1>
                    <p class="text-xs text-gray-500">Each company's latest feedback in any of the client's campaigns.</p>
                </div>
                <label class="flex flex-col gap-1 text-xs font-medium text-gray-600 ml-auto">
                    Client
                    <select
                        class="w-72 h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-accent-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
                        :value="clientId ?? ''"
                        @change="setClient($event.target.value)"
                    >
                        <option value="">Select a client…</option>
                        <option v-for="client in clients" :key="client.id" :value="client.id">{{ client.client_name }}</option>
                    </select>
                </label>
                <div class="flex items-end gap-2">
                    <div class="flex h-9 rounded-md border border-gray-300 overflow-hidden text-sm" role="group" aria-label="Download format">
                        <button
                            v-for="format in DOWNLOAD_FORMATS"
                            :key="format.key"
                            type="button"
                            class="px-3"
                            :class="downloadFormat === format.key
                                ? 'bg-accent-primary text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-50'"
                            :aria-pressed="downloadFormat === format.key"
                            @click="downloadFormat = format.key"
                        >
                            {{ format.label }}
                        </button>
                    </div>
                    <button
                        type="button"
                        class="h-9 px-4 rounded-md bg-dark-primary text-white text-sm font-medium shadow-sm hover:bg-accent-primary disabled:bg-gray-300 disabled:cursor-not-allowed"
                        :disabled="!appendix || isLoading || isDownloading"
                        @click="download"
                    >
                        {{ isDownloading ? 'Building…' : 'Download' }}
                    </button>
                </div>
            </div>
            <div class="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                <span class="text-xs font-medium text-gray-600 mr-1">Columns</span>
                <button
                    v-for="column in columnOptions"
                    :key="column"
                    type="button"
                    class="px-2.5 py-1 rounded-full border text-xs"
                    :class="columnLabels.includes(column)
                        ? 'bg-accent-primary border-accent-primary text-white'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-accent-primary'"
                    :aria-pressed="columnLabels.includes(column)"
                    @click="toggleColumn(column)"
                >
                    {{ column }}
                </button>
                <span class="h-4 border-l border-gray-300 mx-1" aria-hidden="true"></span>
                <button
                    type="button"
                    class="px-2.5 py-1 rounded-full border text-xs"
                    :class="groupByTier
                        ? 'bg-accent-primary border-accent-primary text-white'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-accent-primary'"
                    :aria-pressed="groupByTier"
                    @click="groupByTier = !groupByTier"
                >
                    Group by Tier
                </button>
            </div>
        </div>

        <div v-if="!clientId" class="bg-white border border-dashed border-gray-300 rounded-lg p-10 text-center text-sm text-gray-500">
            Select a client to build its appendix.
        </div>
        <div v-else-if="isLoading" class="flex items-center justify-center gap-2 p-10 text-sm text-gray-500">
            <svg class="w-5 h-5 animate-spin text-accent-primary" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" class="opacity-25" />
                <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
            </svg>
            Loading appendix…
        </div>
        <div v-else-if="appendix" class="flex flex-col gap-5">
            <client-appendix-publications
                :appendix="appendix"
                :columnLabels="columnLabels"
                :groupByTier="groupByTier"
                :viewingId="viewed?.id ?? null"
                :comparingId="compared?.id ?? null"
                @view="viewPublication"
                @compare="comparePublication"
                @deleted="forgetPublication"
            />
            <div v-if="compared" class="flex flex-wrap items-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700">
                <span>
                    Changes since the appendix <span class="font-semibold">published {{ formatDateTime(compared.published_at) }}</span>:
                    <span class="ml-1 rounded bg-green-50 px-1.5 font-mono text-green-800">+ added</span>
                    <span class="ml-1 rounded bg-red-50 px-1.5 font-mono text-red-800">− removed</span>
                </span>
                <button type="button" class="ml-auto font-medium text-gray-700 underline hover:no-underline" @click="comparePublication(null)">
                    Hide changes
                </button>
            </div>
            <div v-if="viewed" class="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
                <span>
                    Viewing the appendix <span class="font-semibold">published {{ formatDateTime(viewed.published_at) }}</span>,
                    not the current one. Downloads are of this version.
                </span>
                <button type="button" class="ml-auto font-medium text-amber-900 underline hover:no-underline" @click="viewPublication(null)">
                    Back to current
                </button>
            </div>
            <section v-for="section in sections" :key="section.key" class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <button
                    type="button"
                    class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50"
                    :class="{ 'border-b border-gray-200': !collapsed[section.key] }"
                    :aria-expanded="!collapsed[section.key]"
                    @click="collapsed[section.key] = !collapsed[section.key]"
                >
                    <ChevronDownMiniSVG v-if="collapsed[section.key]" fill="#25274D" iconClass="h-4 w-4" />
                    <ChevronUpMiniSVG v-else fill="#25274D" iconClass="h-4 w-4" />
                    <h2 class="font-semibold text-dark-primary">{{ section.title }}</h2>
                    <span class="px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">{{ section.count }}</span>
                    <template v-if="compared">
                        <span v-if="section.added" class="font-mono text-xs text-green-700">+{{ section.added }}</span>
                        <span v-if="section.removed" class="font-mono text-xs text-red-700">−{{ section.removed }}</span>
                    </template>
                </button>
                <template v-if="!collapsed[section.key]">
                    <client-appendices-table
                        v-if="section.companies.length"
                        :columnLabels="columnLabels"
                        :groups="section.groups"
                        :showDiff="!!compared"
                    />
                    <div v-else class="px-4 py-6 text-sm text-gray-500">No companies.</div>
                </template>
            </section>
        </div>
    </div>
</template>

<script setup>
    import { ref, reactive, computed } from 'vue';
    import { useStore } from "vuex";
    import { useToast } from 'vue-toast-notification';
    import { getAllClientsSimple } from '@/services/Client.js';
    import { getClientAppendix } from '@/services/Company.js';
    import { CLIENT_APPENDICES, CLIENT_APPENDIX_COLUMNS, downloadClientAppendixDocx, downloadClientAppendixPdf, exhibitDiffRows, formatFileDate, tierGroups } from '@/utils/clientAppendix';
    import ClientAppendicesTable from './ClientAppendicesTable.vue';
    import ClientAppendixPublications from './ClientAppendixPublications.vue';
    import ChevronDownMiniSVG from '../svg-components/ChevronDownMiniSVG.vue';
    import ChevronUpMiniSVG from '../svg-components/ChevronUpMiniSVG.vue';

    // Appendices A (approved), B (excluded) and C (any other feedback) for one client, built from each
    // company's latest feedback in any of the client's campaigns, wherever the company is now.
    const store = useStore();
    const $toast = useToast();

    const isLoading = ref(false);
    const isDownloading = ref(false);

    const clientId = ref(null);
    const clients = ref([]);
    const appendix = ref(null);

    const DOWNLOAD_FORMATS = [
        { key: 'docx', label: 'Word', build: downloadClientAppendixDocx },
        { key: 'pdf', label: 'PDF', build: downloadClientAppendixPdf },
    ];
    const downloadFormat = ref('docx');

    const columnLabels = ref([]);
    const groupByTier = ref(false);
    const columnOptions = CLIENT_APPENDIX_COLUMNS.map((column) => column.label);

    // Sections start open; a collapsed one stays collapsed when the client changes
    const collapsed = reactive({});

    // A published appendix chosen to view replaces the current one in the exhibits and downloads.
    // It's shown with its own columns and grouping; the page's own come back with "Back to current".
    const viewed = ref(null);
    let columnsBeforeViewing = null;

    const shownAppendix = computed(() => (viewed.value
        ? { client: appendix.value.client, ...viewed.value.appendices }
        : appendix.value));

    function viewPublication(publication) {
        if (publication) compared.value = null;
        if (publication && !viewed.value) {
            columnsBeforeViewing = { columnLabels: columnLabels.value, groupByTier: groupByTier.value };
        }
        if (publication) {
            columnLabels.value = [...publication.column_labels];
            groupByTier.value = publication.group_by_tier;
        } else if (viewed.value && columnsBeforeViewing) {
            columnLabels.value = columnsBeforeViewing.columnLabels;
            groupByTier.value = columnsBeforeViewing.groupByTier;
            columnsBeforeViewing = null;
        }
        viewed.value = publication;
    }

    // A published appendix chosen to compare with: the current exhibits then also show, git style, the
    // companies added (+) since it was published and the ones no longer there (−), as they were then
    const compared = ref(null);

    function comparePublication(publication) {
        if (publication) viewPublication(null);
        compared.value = publication;
    }

    function forgetPublication(id) {
        if (viewed.value?.id === id) viewPublication(null);
        if (compared.value?.id === id) compared.value = null;
    }

    function formatDateTime(dateString) {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    }

    const sections = computed(() => {
        if (!shownAppendix.value) return [];
        return CLIENT_APPENDICES.map(({ key, title, tierGroupable }) => {
            const companies = shownAppendix.value[key];
            const rows = compared.value ? exhibitDiffRows(compared.value.appendices, shownAppendix.value, key) : companies;
            // One unlabeled group when not grouping, so the table renders the same way either way
            const groups = groupByTier.value && tierGroupable ? tierGroups(rows) : [{ label: null, companies: rows }];
            return {
                key, title, companies: rows, groups, count: companies.length,
                added: rows.filter((row) => row.diff === '+').length,
                removed: rows.filter((row) => row.diff === '-').length,
            };
        });
    });

    function toggleColumn(label) {
        columnLabels.value = columnLabels.value.includes(label)
            ? columnLabels.value.filter((selected) => selected !== label)
            : [...columnLabels.value, label];
    }

    // Each load is numbered so a slower response for a client that has since changed is dropped
    let latestRequest = 0;

    function setClient(target) {
        clientId.value = target ? Number(target) : null;
        viewPublication(null);
        compared.value = null;
        appendix.value = null;
        if (clientId.value) {
            loadAppendix();
        } else {
            latestRequest += 1;
            isLoading.value = false;
        }
    }

    async function loadClients() {
        const result = await getAllClientsSimple({ token: store.state.auth.token });
        clients.value = result.data;
    }

    async function loadAppendix() {
        const request = ++latestRequest;
        isLoading.value = true;
        const result = await getClientAppendix(clientId.value, store.state.auth.token);
        if (request !== latestRequest) return;
        isLoading.value = false;
        if (result.status !== 200) {
            $toast.open({ type: 'error', duration: 10000, message: 'Could not load the client appendix. Try again.' });
            return;
        }
        appendix.value = result.data;
    }

    async function download() {
        const format = DOWNLOAD_FORMATS.find(({ key }) => key === downloadFormat.value);
        isDownloading.value = true;
        try {
            // A viewed publication's files are named for the day it was published
            const suffix = viewed.value ? formatFileDate(viewed.value.published_at) : '';
            await format.build(shownAppendix.value, columnLabels.value, groupByTier.value, suffix);
        } catch (error) {
            console.log(error);
            $toast.open({ type: 'error', duration: 10000, message: `Could not build the ${format.label} document.` });
        } finally {
            isDownloading.value = false;
        }
    }

    loadClients();
</script>
